import type { LocationQuery } from 'vue-router';

import type { AgentSearchRequest } from '@/api/agentMetaApi';
import { AgentService, AgentSpecialization } from '@/types/agentProfile';

export interface AgentSearchFacets {
  services: AgentService[];
  specializations: AgentSpecialization[];
}

export const emptyAgentSearchFacets = (): AgentSearchFacets => ({
  services: [],
  specializations: [],
});

const SERVICE_VALUES = new Set<string>(Object.values(AgentService));
const SPECIALIZATION_VALUES = new Set<string>(Object.values(AgentSpecialization));

const parseCommaSeparatedEnumValues = <T extends string>(
  raw: LocationQuery[string],
  allowed: Set<string>,
): T[] => {
  if (raw === undefined || raw === null || raw === '') {
    return [];
  }

  const parts = Array.isArray(raw)
    ? raw.flatMap((item) => String(item).split(','))
    : String(raw).split(',');

  const unique = new Set<T>();
  for (const part of parts) {
    const trimmed = part.trim();
    if (allowed.has(trimmed)) {
      unique.add(trimmed as T);
    }
  }

  return [...unique].sort((a, b) => a.localeCompare(b));
};

export const parseFacetQuery = (query: LocationQuery): AgentSearchFacets => ({
  services: parseCommaSeparatedEnumValues<AgentService>(query.services, SERVICE_VALUES),
  specializations: parseCommaSeparatedEnumValues<AgentSpecialization>(
    query.specializations,
    SPECIALIZATION_VALUES,
  ),
});

export const buildFacetQuery = (
  facets: AgentSearchFacets,
): { services?: string; specializations?: string } => {
  const query: { services?: string; specializations?: string } = {};

  if (facets.services.length > 0) {
    query.services = [...facets.services].sort((a, b) => a.localeCompare(b)).join(',');
  }

  if (facets.specializations.length > 0) {
    query.specializations = [...facets.specializations]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
  }

  return query;
};

const isAgentServiceKey = (key: string): key is AgentService => SERVICE_VALUES.has(key);

const isAgentSpecializationKey = (key: string): key is AgentSpecialization =>
  SPECIALIZATION_VALUES.has(key);

export const toggleFacet = (facets: AgentSearchFacets, key: string): AgentSearchFacets => {
  if (isAgentServiceKey(key)) {
    const services = facets.services.includes(key)
      ? facets.services.filter((value) => value !== key)
      : [...facets.services, key];

    return { ...facets, services };
  }

  if (isAgentSpecializationKey(key)) {
    const specializations = facets.specializations.includes(key)
      ? facets.specializations.filter((value) => value !== key)
      : [...facets.specializations, key];

    return { ...facets, specializations };
  }

  return facets;
};

export const toRequestFacets = (
  facets: AgentSearchFacets,
): AgentSearchRequest['facets'] | undefined => {
  if (facets.services.length === 0 && facets.specializations.length === 0) {
    return undefined;
  }

  return {
    ...(facets.services.length > 0 ? { services: facets.services } : {}),
    ...(facets.specializations.length > 0 ? { specializations: facets.specializations } : {}),
  };
};

const sortedFacetKeys = (
  facets: AgentSearchFacets,
): { services: string[]; specializations: string[] } => ({
  services: [...facets.services].sort((a, b) => a.localeCompare(b)),
  specializations: [...facets.specializations].sort((a, b) => a.localeCompare(b)),
});

export const isSameAgentSearchFacets = (
  left: AgentSearchFacets,
  right: AgentSearchFacets,
): boolean => {
  const a = sortedFacetKeys(left);
  const b = sortedFacetKeys(right);

  return (
    a.services.length === b.services.length &&
    a.specializations.length === b.specializations.length &&
    a.services.every((value, index) => value === b.services[index]) &&
    a.specializations.every((value, index) => value === b.specializations[index])
  );
};
